// QR Kiosk v2 — bottom sheet opened by the tier chip: Settings / Language /
// Sign out. Controlled by state.kioskV2.accountSheetOpen.
//
// Round-6: Dialog.Portal into the kiosk overlay root (`useKioskOverlayRoot`). The old
// KV2-0-b "no Portal" note is superseded — Radix's DEFAULT portal mounts on document.body
// (outside .qr-kiosk-v2-root, tokens don't resolve); portaling into the SCOPED root keeps
// the tokens AND escapes any animated page subtree (uniform with every other overlay).
import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { closeAccountSheet } from "../kioskV2Slice";
import { useKioskOverlayRoot } from "../shared/kioskOverlayRoot";
import { Glyph, GD } from "../shared/glyphs";
import accountContext from "../../../account/accountContext";
import { refreshAccount } from "../../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../../account/routes";
import { clearGuestSession, isGuestRole } from "../guest/guestSession";

interface SheetRowProps {
  leading: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}

function SheetRow({ leading, label, danger, onClick }: SheetRowProps) {
  const [active, setActive] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onPointerDown={() => setActive(true)}
      onPointerUp={() => setActive(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        height: 52,
        padding: "0 12px",
        borderRadius: 11,
        border: 0,
        background: active ? "var(--bg-soft)" : "transparent",
        color: danger ? "var(--red)" : "var(--ink-2)",
        fontFamily: "inherit",
        fontSize: 14.5,
        fontWeight: 600,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      {leading}
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

export function AccountSheet() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, translate } = useTranslation("public");
  const { isPhone, isTablet } = useBreakpoint();
  const overlayRoot = useKioskOverlayRoot();

  const open = useAppSelector((s) => s.kioskV2.accountSheetOpen);
  const user = useAppSelector((s) => s.account.user);

  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const role = (user?.role || "").toLowerCase();
  const lang = i18n.language.split("-")[0].toUpperCase();

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;

  const machineMatch = location.pathname.match(/\/machines\/([^/]+)/);
  const machineNumber = machineMatch ? machineMatch[1] : undefined;

  const goToSettings = () => {
    dispatch(closeAccountSheet());
    if (machineNumber) navigate(`/public-v2/machines/${machineNumber}/settings`);
  };

  const handleSignOut = () => {
    dispatch(closeAccountSheet());
    // KV2-8/T1: a guest has no company credentials — sending them to the company login is a
    // dead end. Clear the guest session (token + companion) and return to the machine-scoped
    // entry chooser when we have machine context (§3.3), else the standalone landing fallback.
    if (isGuestRole(user?.role)) {
      clearGuestSession();
      dispatch(refreshAccount(null));
      navigate(machineNumber ? `/public-v2/machines/${machineNumber}` : "/public-v2/guest", { replace: true });
      return;
    }
    // v1 parity verbatim (KioskDashboard.handleLogout) — sign in with a return URL.
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) dispatch(closeAccountSheet());
      }}
    >
      <Dialog.Portal container={overlayRoot}>
      <Dialog.Overlay style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 40 }} />
      <Dialog.Content
        aria-describedby={undefined}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 41,
          maxWidth: columnMaxWidth,
          margin: "0 auto",
          background: "var(--card)",
          borderTop: "1px solid var(--border)",
          borderRadius: "16px 16px 0 0",
          padding: "10px 14px calc(14px + env(safe-area-inset-bottom))",
          boxShadow: "var(--shadow)",
        }}
      >
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px 10px" }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: "var(--tier-accent)",
              color: "var(--tier-accent-fg)",
              display: "inline-grid",
              placeItems: "center",
              fontSize: 10,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials}
          </span>
          <div style={{ minWidth: 0 }}>
            <Dialog.Title asChild>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--ink-2)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fullName}
              </div>
            </Dialog.Title>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)" }}>{role}</div>
          </div>
        </div>

        {/* Rows */}
        {machineNumber && (
          <SheetRow
            leading={<Glyph d={GD.gear} size={20} style={{ color: "var(--muted)" }} />}
            label={translate("kioskSettings")}
            onClick={goToSettings}
          />
        )}
        {machineNumber && (
          <SheetRow
            leading={
              <span
                style={{
                  width: 20,
                  textAlign: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10.5,
                  color: "var(--muted)",
                  letterSpacing: "0.04em",
                  flexShrink: 0,
                }}
              >
                {lang}
              </span>
            }
            label={translate("kioskLanguage")}
            onClick={goToSettings}
          />
        )}
        <SheetRow
          leading={<Glyph d={GD.out} size={20} />}
          label={translate("kioskLogout")}
          danger
          onClick={handleSignOut}
        />
      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default AccountSheet;
